// Qui Est-Ce ? - Devine le joueur avec des indices progressifs
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ArrowLeft, Loader2, Trophy, RefreshCw, Eye, User, Flag, Calendar, Hash, HelpCircle } from 'lucide-react';
import { GAMES_CONFIG, calculatePoints, DIFFICULTY_MULTIPLIERS } from '../services/fanZoneConfig';
import { saveGameScore, hasPlayedToday, getDailyChallenge, createDailyChallenge } from '../services/fanZoneService';
import { db } from '../../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import logger from '../../../utils/logger';
import toast from 'react-hot-toast';

// Scrollbar styles
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar { width: 6px; }
  .fanzone-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

const INDICES_CONFIG = [
  { id: 'poste', icon: User, label: 'Poste', getValue: (p) => p?.poste || 'Inconnu' },
  { id: 'nationalite', icon: Flag, label: 'Nationalité', getValue: (p) => p?.nationalite || 'Française' },
  { id: 'age', icon: Calendar, label: 'Tranche d\'âge', getValue: (p) => {
    if (!p) return 'Inconnu';
    const age = p.age || 25;
    if (age < 23) return 'Moins de 23 ans';
    if (age <= 28) return '23-28 ans';
    return 'Plus de 28 ans';
  }},
  { id: 'numero', icon: Hash, label: 'Numéro', getValue: (p) => p?.numero ? `N°${p.numero}` : 'Non attribué' },
  { id: 'silhouette', icon: HelpCircle, label: 'Silhouette', getValue: () => 'Photo floue' }
];

const IndiceCard = ({ indice, isRevealed, onReveal, player, disabled }) => {
  const Icon = indice.icon;
  
  return (
    <motion.button
      whileHover={!isRevealed && !disabled ? { scale: 1.02 } : {}}
      whileTap={!isRevealed && !disabled ? { scale: 0.98 } : {}}
      onClick={() => !isRevealed && !disabled && onReveal()}
      disabled={isRevealed || disabled}
      className={`
        w-full p-4 rounded-xl border transition-all duration-300
        ${isRevealed 
          ? 'bg-[#FFE400]/10 border-[#FFE400]/30' 
          : disabled 
            ? 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 cursor-pointer'
        }
      `}
    >
      <div className="flex items-center gap-4">
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center
          ${isRevealed ? 'bg-[#FFE400]/20' : 'bg-white/10'}
        `}>
          {isRevealed ? (
            <Icon className="w-6 h-6 text-[#FFE400]" />
          ) : (
            <Eye className="w-6 h-6 text-gray-400" />
          )}
        </div>
        
        <div className="flex-1 text-left">
          <div className="text-sm text-gray-400">{indice.label}</div>
          {isRevealed ? (
            <div className="text-lg font-bold text-white">
              {indice.getValue(player)}
            </div>
          ) : (
            <div className="text-lg font-medium text-gray-500">
              Cliquer pour révéler
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
};

const PlayerOption = ({ player, onClick, disabled }) => {
  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={onClick}
      disabled={disabled}
      className={`
        p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#FFE400]/30 
        transition-all duration-200 text-left
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFE400]/20 to-[#BDFF00]/10 flex items-center justify-center text-white font-bold">
          {player.prenom?.charAt(0) || player.nom?.charAt(0) || '?'}
        </div>
        <div>
          <div className="font-bold text-white">{player.prenom} {player.nom}</div>
          <div className="text-sm text-gray-400">{player.poste}</div>
        </div>
      </div>
    </motion.button>
  );
};

const QuiEstCeGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  
  const difficulty = searchParams.get('difficulty') || 'facile';
  const config = GAMES_CONFIG.quiEstCe;
  
  const [loading, setLoading] = useState(true);
  const [targetPlayer, setTargetPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [revealedIndices, setRevealedIndices] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlayers, setShowPlayers] = useState(false);

  // Injecter styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Charger le jeu
  useEffect(() => {
    const loadGame = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        // Joueurs de démonstration (fallback)
        const demoPlayers = [
          { id: 'demo1', nom: 'DUMONT', prenom: 'Lucas', poste: 'Milieu', nationalite: 'Française', age: 24, numero: 8 },
          { id: 'demo2', nom: 'MARTIN', prenom: 'Pierre', poste: 'Défenseur', nationalite: 'Française', age: 27, numero: 4 },
          { id: 'demo3', nom: 'BERNARD', prenom: 'Thomas', poste: 'Attaquant', nationalite: 'Française', age: 22, numero: 9 },
          { id: 'demo4', nom: 'PETIT', prenom: 'Antoine', poste: 'Gardien', nationalite: 'Française', age: 30, numero: 1 },
          { id: 'demo5', nom: 'ROBERT', prenom: 'Maxime', poste: 'Milieu', nationalite: 'Belge', age: 26, numero: 10 },
          { id: 'demo6', nom: 'DUBOIS', prenom: 'Julien', poste: 'Défenseur', nationalite: 'Française', age: 25, numero: 3 },
        ];
        
        // Vérifier si déjà joué (ignorer les erreurs de permission)
        try {
          const played = await hasPlayedToday(currentUser.uid, 'quiEstCe', difficulty);
          if (played) {
            setAlreadyPlayed(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour hasPlayedToday');
        }

        // Charger les joueurs
        let allPlayers = [];
        try {
          const joueursSnapshot = await getDocs(collection(db, 'joueurs'));
          joueursSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.nom) {
              allPlayers.push({ id: doc.id, ...data });
            }
          });
        } catch (e) {
          logger.warn('Erreur chargement joueurs, utilisation démo');
        }

        // Fallback si pas de joueurs
        if (allPlayers.length === 0) {
          allPlayers = [...demoPlayers];
        }

        setPlayers(allPlayers);

        // Récupérer ou créer le défi du jour (mode fallback si erreur)
        let challenge = null;
        try {
          challenge = await getDailyChallenge(`quiEstCe_${difficulty}`);
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour getDailyChallenge');
        }
        
        if (!challenge) {
          const randomPlayer = allPlayers[Math.floor(Math.random() * allPlayers.length)];
          challenge = {
            playerId: randomPlayer.id,
            playerData: randomPlayer
          };
          // Essayer de sauvegarder, ignorer si permission refusée
          try {
            await createDailyChallenge(`quiEstCe_${difficulty}`, challenge);
          } catch (e) {
            logger.warn('Impossible de sauvegarder le défi, mode démo actif');
          }
        }

        const target = allPlayers.find(p => p.id === challenge.playerId) || challenge.playerData;
        setTargetPlayer(target);
        
      } catch (error) {
        logger.error('Erreur chargement Qui Est-Ce:', error);
        // En cas d'erreur totale, charger en mode démo
        const demoPlayers = [
          { id: 'demo1', nom: 'DUMONT', prenom: 'Lucas', poste: 'Milieu', nationalite: 'Française', age: 24, numero: 8 },
          { id: 'demo2', nom: 'MARTIN', prenom: 'Pierre', poste: 'Défenseur', nationalite: 'Française', age: 27, numero: 4 },
          { id: 'demo3', nom: 'BERNARD', prenom: 'Thomas', poste: 'Attaquant', nationalite: 'Française', age: 22, numero: 9 },
        ];
        setPlayers(demoPlayers);
        setTargetPlayer(demoPlayers[Math.floor(Math.random() * demoPlayers.length)]);
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [currentUser, difficulty, navigate]);

  const handleRevealIndice = (indiceId) => {
    if (revealedIndices.includes(indiceId)) return;
    setRevealedIndices(prev => [...prev, indiceId]);
  };

  const handleGuess = async (player) => {
    const isCorrect = player.id === targetPlayer.id;
    setGameOver(true);
    setWon(isCorrect);
    
    const points = isCorrect ? calculatePoints.quiEstCe(revealedIndices.length, difficulty) : 0;
    setPointsEarned(points);
    
    if (currentUser) {
      try {
        await saveGameScore(currentUser.uid, 'quiEstCe', difficulty, points, {
          won: isCorrect,
          indicesUsed: revealedIndices.length,
          targetPlayer: `${targetPlayer.prenom} ${targetPlayer.nom}`,
          guessedPlayer: `${player.prenom} ${player.nom}`
        });
      } catch (err) {
        logger.error('Erreur sauvegarde score:', err);
      }
    }
  };

  const filteredPlayers = players.filter(p => 
    `${p.prenom} ${p.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 8);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  if (alreadyPlayed) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <RefreshCw className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Déjà joué aujourd'hui !</h2>
          <p className="text-gray-400 mb-6">Reviens demain pour un nouveau défi</p>
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
          >
            Retour au dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
    >
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-sm">
            <span className="text-gray-400">Difficulté:</span>
            <span className="text-[#FFE400] font-medium">
              {config.difficulties[difficulty]?.label} (x{DIFFICULTY_MULTIPLIERS[difficulty]})
            </span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="text-4xl mb-2">🔍</div>
          <h1 className="text-2xl font-bold text-white">Qui est-ce ?</h1>
          <p className="text-gray-400 text-sm mt-1">
            Révèle des indices pour deviner le joueur
          </p>
        </motion.div>

        {/* Score potentiel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-3 rounded-xl bg-white/5 border border-white/10 text-center"
        >
          <span className="text-gray-400 text-sm">Points potentiels: </span>
          <span className="text-[#BDFF00] font-bold">
            {calculatePoints.quiEstCe(revealedIndices.length, difficulty)} XP
          </span>
          <span className="text-gray-500 text-sm"> ({revealedIndices.length}/{config.maxIndices} indices)</span>
        </motion.div>

        {/* Indices */}
        <div className="space-y-3 mb-8">
          {INDICES_CONFIG.map((indice, index) => (
            <motion.div
              key={indice.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <IndiceCard
                indice={indice}
                isRevealed={revealedIndices.includes(indice.id)}
                onReveal={() => handleRevealIndice(indice.id)}
                player={targetPlayer}
                disabled={gameOver}
              />
            </motion.div>
          ))}
        </div>

        {/* Deviner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={() => setShowPlayers(!showPlayers)}
            className="w-full py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl mb-4"
          >
            {showPlayers ? 'Masquer les joueurs' : 'Faire une proposition'}
          </button>

          <AnimatePresence>
            {showPlayers && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <input
                  type="text"
                  placeholder="Rechercher un joueur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 mb-3 focus:outline-none focus:border-[#FFE400]/50"
                />
                
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {filteredPlayers.map(player => (
                    <PlayerOption
                      key={player.id}
                      player={player}
                      onClick={() => handleGuess(player)}
                      disabled={gameOver}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Game Over Modal */}
        <AnimatePresence>
          {gameOver && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="bg-[#0d1526] border border-white/10 rounded-2xl p-8 max-w-sm w-full text-center"
              >
                {won ? (
                  <>
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Bravo !</h2>
                    <p className="text-gray-400 mb-4">
                      C'était bien <span className="text-[#FFE400] font-bold">{targetPlayer.prenom} {targetPlayer.nom}</span> !
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      Trouvé avec {revealedIndices.length} indice{revealedIndices.length > 1 ? 's' : ''}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-4">😔</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Perdu...</h2>
                    <p className="text-gray-400 mb-4">
                      C'était <span className="text-[#FFE400] font-bold">{targetPlayer.prenom} {targetPlayer.nom}</span>
                    </p>
                  </>
                )}
                
                {pointsEarned > 0 && (
                  <div className="flex items-center justify-center gap-2 mb-6 text-[#BDFF00]">
                    <Trophy className="w-5 h-5" />
                    <span className="text-xl font-bold">+{pointsEarned} XP</span>
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/fanzone/dashboard')}
                  className="w-full py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
                >
                  Retour au dashboard
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuiEstCeGame;
